import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, Eye } from 'lucide-react';

interface ParsedOpportunity {
  title: string;
  company: string;
  location: string;
  type: 'emploi' | 'stage' | 'formation' | 'bourse' | 'volontariat';
  description: string;
  requirements?: string[];
  deadline?: string;
  applicationUrl?: string;
  salary?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export function OpportunityQuickImport() {
  const [inputData, setInputData] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<ParsedOpportunity[]>([]);
  const { toast } = useToast();

  const parseJobData = (data: string): ParsedOpportunity[] => {
    const opportunities: ParsedOpportunity[] = [];
    
    // Format attendu:
    // Titre: xxx
    // Entreprise: xxx
    // Lieu: xxx
    // Type: emploi/stage/formation/bourse/volontariat
    // Description: xxx
    // Exigences: xxx
    // Date limite: xxx
    // Lien de candidature: xxx
    // ---
    
    const blocks = data.split('---').filter(block => block.trim());
    
    blocks.forEach(block => {
      const lines = block.split('\n').filter(line => line.trim());
      const opp: any = {};
      
      lines.forEach(line => {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          const normalizedKey = key.trim().toLowerCase();
          
          if (normalizedKey.includes('titre')) opp.title = value;
          else if (normalizedKey.includes('entreprise') || normalizedKey.includes('organisation')) opp.company = value;
          else if (normalizedKey.includes('lieu') || normalizedKey.includes('location')) opp.location = value;
          else if (normalizedKey.includes('type')) {
            const typeValue = value.toLowerCase();
            if (typeValue.includes('emploi') || typeValue.includes('job')) opp.type = 'emploi';
            else if (typeValue.includes('stage') || typeValue.includes('intern')) opp.type = 'stage';
            else if (typeValue.includes('formation') || typeValue.includes('training')) opp.type = 'formation';
            else if (typeValue.includes('bourse') || typeValue.includes('scholarship')) opp.type = 'bourse';
            else if (typeValue.includes('volontariat') || typeValue.includes('volunteer')) opp.type = 'volontariat';
            else opp.type = 'emploi';
          }
          else if (normalizedKey.includes('description')) opp.description = value;
          else if (normalizedKey.includes('exigences') || normalizedKey.includes('requirements')) {
            opp.requirements = value.split(',').map(r => r.trim());
          }
          else if (normalizedKey.includes('date limite') || normalizedKey.includes('deadline')) opp.deadline = value;
          else if (normalizedKey.includes('lien') || normalizedKey.includes('url')) opp.applicationUrl = value;
          else if (normalizedKey.includes('salaire') || normalizedKey.includes('salary')) opp.salary = value;
          else if (normalizedKey.includes('email')) opp.contactEmail = value;
          else if (normalizedKey.includes('téléphone') || normalizedKey.includes('phone')) opp.contactPhone = value;
        }
      });
      
      // Validation basique
      if (opp.title && opp.company && opp.location && opp.description) {
        opportunities.push(opp as ParsedOpportunity);
      }
    });
    
    return opportunities;
  };

  const handlePreview = () => {
    try {
      const parsed = parseJobData(inputData);
      if (parsed.length === 0) {
        toast({
          title: 'Aucune opportunité trouvée',
          description: 'Vérifiez le format de vos données',
          variant: 'destructive',
        });
        return;
      }
      setPreview(parsed);
      toast({
        title: 'Aperçu généré',
        description: `${parsed.length} opportunité(s) détectée(s)`,
      });
    } catch (error) {
      toast({
        title: 'Erreur de parsing',
        description: 'Format de données invalide',
        variant: 'destructive',
      });
    }
  };

  const handleImport = async () => {
    if (preview.length === 0) {
      toast({
        title: 'Aucune opportunité',
        description: 'Générez d\'abord un aperçu',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const opportunities = preview.map(opp => ({
        title: opp.title,
        company: opp.company,
        location: opp.location,
        type: opp.type,
        description: opp.description,
        requirements: opp.requirements || null,
        deadline: opp.deadline ? new Date(opp.deadline).toISOString() : null,
        application_url: opp.applicationUrl || null,
        salary: opp.salary || null,
        contact_email: opp.contactEmail || null,
        contact_phone: opp.contactPhone || null,
        status: 'approved' as const, // Auto-approve for admin imports
      }));

      const { error } = await supabase
        .from('opportunities')
        .insert(opportunities);

      if (error) throw error;

      toast({
        title: 'Import réussi',
        description: `${opportunities.length} opportunité(s) ajoutée(s)`,
      });

      setInputData('');
      setPreview([]);
    } catch (error: any) {
      toast({
        title: 'Erreur d\'import',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Import Rapide d'Opportunités</CardTitle>
          <CardDescription>
            Collez vos données structurées pour importer plusieurs opportunités à la fois
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Format attendu:</label>
            <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
{`Titre: Économiste en Agriculture
Entreprise: Banque mondiale
Lieu: Brazzaville, République du Congo
Type: emploi
Description: Support du programme agricole et alimentaire...
Exigences: Master en économie agricole, 5 ans d'expérience
Date limite: 2025-12-01
Lien de candidature: https://example.com/apply
---
Titre: Autre opportunité
...`}
            </pre>
          </div>

          <Textarea
            placeholder="Collez vos données ici..."
            value={inputData}
            onChange={(e) => setInputData(e.target.value)}
            className="min-h-[300px] font-mono text-sm"
          />

          <div className="flex gap-3">
            <Button onClick={handlePreview} variant="outline">
              <Eye className="mr-2 h-4 w-4" />
              Aperçu
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={loading || preview.length === 0}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Importer {preview.length > 0 && `(${preview.length})`}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Aperçu ({preview.length} opportunité(s))</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {preview.map((opp, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-2">
                  <h3 className="font-semibold">{opp.title}</h3>
                  <p className="text-sm text-muted-foreground">{opp.company} • {opp.location}</p>
                  <p className="text-sm">{opp.description.substring(0, 150)}...</p>
                  <div className="flex gap-2">
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      {opp.type}
                    </span>
                    {opp.deadline && (
                      <span className="text-xs bg-secondary/10 text-secondary-foreground px-2 py-1 rounded">
                        Date limite: {opp.deadline}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
